import { useCallback, useEffect, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import {
	SEARCH_LOCATION_QUERY_KEY,
	SEARCH_LOCATION_SUGGESTIONS_MAX_COUNTRIES,
	SEARCH_LOCATION_SUGGESTIONS_MAX_ITEMS,
} from '#src/app/pages/search/shared/consts';
import type { LocationItem, LocationItemTypeEnum } from '#src/app/pages/search/shared/types';

import { useClearSelectedLocation } from './hooks/useClearSelectedLocation';
import { useLocationSearchCities } from './hooks/useLocationSearchCities';
import { useLocationSearchCountries } from './hooks/useLocationSearchCountries';
import { useSelectLocation } from './hooks/useSelectLocation';
import { mergeLocationSuggestions } from './utils';

type Result = ModelHookResult<
	{
		locations: readonly LocationItem[];
		isLoading: boolean;
		isError: boolean;
		selectedLocation: string | undefined;
	},
	{
		searchDebounced: (value: string) => void;
		onResetQuery: () => void;
		selectLocation: (value: string, type: LocationItemTypeEnum, mergedValues?: readonly string[]) => void;
	}
>;

// eslint-disable-next-line max-lines-per-function
export function useLocationSearch(): Result {
	// THIS STATE IS USED TO DEBOUNCE THE QUERY CALL
	const [queryLocation, setQueryLocation] = useState('');

	const {
		actions: { searchForCities },
	} = useLocationSearchCities();

	const {
		actions: { searchForCountries },
	} = useLocationSearchCountries();

	const {
		actions: { selectLocation },
	} = useSelectLocation();

	const {
		data: { selectedLocation },
		actions: { clearSelectedLocation },
	} = useClearSelectedLocation();

	const {
		data: locations,
		isLoading,
		isError,
	} = useQuery({
		queryKey: [SEARCH_LOCATION_QUERY_KEY, queryLocation, selectedLocation],
		queryFn: async () => {
			const [cities, countries] = await Promise.all([
				searchForCities(queryLocation, selectedLocation),
				searchForCountries(queryLocation, selectedLocation),
			]);

			// without a query these are the most relevant locations, already in the order we want
			if (!queryLocation) return [...cities, ...countries];

			return mergeLocationSuggestions(cities, countries, {
				query: queryLocation,
				maxItems: SEARCH_LOCATION_SUGGESTIONS_MAX_ITEMS,
				maxCountries: SEARCH_LOCATION_SUGGESTIONS_MAX_COUNTRIES,
			});
		},
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});

	const debounceTimeout = useRef<number | null>(null);

	// clean up timeout on unmount
	useEffect(() => {
		return () => {
			if (debounceTimeout.current) {
				window.clearTimeout(debounceTimeout.current);
			}
		};
	}, []);

	return {
		data: {
			locations: locations || [],
			isLoading,
			isError,
			selectedLocation,
		},
		actions: {
			searchDebounced: useCallback((value: string) => {
				if (debounceTimeout.current) {
					window.clearTimeout(debounceTimeout.current);
				}
				debounceTimeout.current = window.setTimeout(() => {
					setQueryLocation(value);
				}, 300);
			}, []),
			onResetQuery: useCallback(() => {
				setQueryLocation('');
				clearSelectedLocation();
			}, [clearSelectedLocation]),
			selectLocation,
		},
	};
}
